import chartTheme from "@components/Charts/chartTheme";
import { ClientContext } from "@context/Client/ClientContext";
import { BarChart, BarChartTooltip } from "@dive.tech/ui-graphs";
import {
  Box,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useModelScenario } from "@services/api";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface MediaSpendScenarioChartProps {
  scenario1Id: string;
  scenario2Id: string;
}

const MediaSpendScenarioChart = ({
  scenario1Id,
  scenario2Id
}: MediaSpendScenarioChartProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdValue = theme.breakpoints.values.md; // 900
  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(mdValue);

  const { data: scenario1, isFetching: isLoadingScenario1 } = useModelScenario(
    scenario1Id ?? ""
  );
  const { data: scenario2, isFetching: isLoadingScenario2 } = useModelScenario(
    scenario2Id ?? ""
  );

  const isLoading = useMemo(() => {
    return isLoadingScenario1 || isLoadingScenario2;
  }, [isLoadingScenario1, isLoadingScenario2]);

  const scenario1Name = useMemo(() => {
    return scenario1?.scenario.name || "Scenario 1";
  }, [scenario1]);

  const scenario2Name = useMemo(() => {
    return scenario2?.scenario.name || "Scenario 2";
  }, [scenario2]);

  const barData = useMemo(() => {
    const sc1media = scenario1?.scenario.media_investment || 0;
    const sc2media = scenario2?.scenario.media_investment || 0;

    return {
      data: [
        {
          name: scenario1Name,
          [currentClient.client.kpi_label]: sc1media
        },
        {
          name: scenario2Name,
          [currentClient.client.kpi_label]: sc2media
        }
      ],
      max: Math.max(sc1media, sc2media) * 1.1
    };
  }, [
    scenario1,
    scenario2,
    currentClient.client.kpi_label,
    scenario1Name,
    scenario2Name
  ]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box className="w-full">
      <Typography variant="h6" component="div" className="pt-4 pl-6">
        {t("simulator.benchmark.media_spend_per_scenario", {
          currency: currentClient.client.currency
        })}
      </Typography>
      <Box sx={{ height: "500px" }} ref={chartContainerRef}>
        {isLoading && <Skeleton variant="rectangular" height={500} />}
        {!isLoading && barData && (
          <BarChart
            data={barData.data}
            responsiveBarProps={{
              theme: chartTheme,
              tooltip: (props: any) =>
                BarChartTooltip({
                  color: props.color,
                  label: props.label,
                  value: props.value,
                  id: props.id,
                  index: props.index,
                  indexValue: props.indexValue,
                  data: props.data,
                  yAxis: { yType: "number", yDecimals: 0 },
                  formattedValue: props.formattedValue,
                  hidden: props.hidden
                }),
              layers: ["grid", "markers", "axes", "legends", "bars"],
              enableGridX: false,
              enableLabel: false,
              groupMode: "grouped",
              indexScale: { type: "band", round: true },
              valueScale: { type: "linear", min: 0, max: barData.max },
              indexBy: "name",
              keys: [currentClient.client.kpi_label],
              colors: ["#6172F3", "#85E139"],
              margin: { top: 50, left: 100, right: 10, bottom: 100 },
              axisLeft: {
                tickSize: 10,
                legendOffset: -80,
                legendPosition: "middle",
                legend: t("mmmodel.kpi_calculator.media_spend_total", {
                  currency: currentClient.client.currency
                })
                //format: (value) => `${Number(value) / 1_000}k`
              },
              axisBottom: {
                legend: t("simulator.scenario.scenario"),
                legendPosition: "middle",
                legendOffset: 70,
                tickRotation: isMobile ? -25 : 0
              },
              legends: [],
              width: chartWidth,
              height: 500
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default MediaSpendScenarioChart;
